/**
 *
 * FormAutoCompleteTextField
 *
 */
import { AsseticField, FormSearchableControl } from '@assetic/web-components';
import { FastFieldConfig, useField } from 'formik';
import React, { useEffect, useState, ReactNode, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { searchableItems } from 'types/models';
import { usePaginationSlice } from '../Pagination/slice';
import { selectPaginationContentAsPreSearchable } from '../Pagination/slice/selectors';
import { PaginationType } from '../Pagination/slice/types';
import {
  ComplexFilterFieldsType,
  pagiFilterFieldsGen,
} from '../PagiQueryTextField';
import { ConvertModelToSearchableFn } from './helpers';

interface Props extends FastFieldConfig<any> {
  label: ReactNode;
  filterFields: ComplexFilterFieldsType;
  path: string;
  required?: boolean;
  disabled?: boolean;
  generateHelperText?: (
    searchableItems: searchableItems,
    value: string,
  ) => ReactNode;
  helperText?: ReactNode;
  convertToSearchable: ConvertModelToSearchableFn;
  paginationType: PaginationType;
}

export function FormSearchableTextField(props: Props) {
  const {
    name,
    label,
    path,
    filterFields,
    required = false,
    disabled = false,
    generateHelperText,
    helperText: providedHelperText,
    convertToSearchable,
    paginationType,
  } = props;

  const [helperTextState, setHelperTextState] = useState<ReactNode>();

  const {
    actions: { initPageData, clearData },
  } = usePaginationSlice();

  const dispatch = useDispatch();

  const [fieldInput, fieldMeta, fieldHelper] = useField(name);

  const data = useSelector(
    selectPaginationContentAsPreSearchable(paginationType),
  );

  const { data: searchableItems } = convertToSearchable(data);

  /**
   * When there is a default value for the textField whereas the selection array is empty
   */
  if (
    !searchableItems.find(item => item.value === fieldMeta.initialValue) && //Prevent duplicate search result
    fieldMeta.initialValue && //Has initial value
    fieldInput.value === fieldMeta.initialValue //IsDirty
  ) {
    searchableItems.push({
      label: fieldMeta.initialValue,
      value: fieldMeta.initialValue,
    });
  }

  const handleSearch = (query: string) => {
    dispatch(
      initPageData({
        filters: pagiFilterFieldsGen(filterFields, query),
        sorts: '',
        page: 0,
        page_size: 6,
        path: path,
        pagiType: paginationType,
      }),
    );
  };

  const clearSearchResult = () => {
    dispatch(clearData(paginationType));
  };

  useEffect(() => {
    const value = fieldInput.value ?? '';
    if (value && !!value.trim()) {
      handleSearch(value);
    }
    if (value === '') {
      clearSearchResult();
    }
  }, [fieldInput.value]);

  const handleOnBlur = () => {
    if (providedHelperText !== null && providedHelperText !== undefined) {
      setHelperTextState(providedHelperText);
    } else if (generateHelperText) {
      setHelperTextState(generateHelperText(searchableItems, fieldInput.value));
    }
  };

  return (
    <AsseticField
      name={name}
      label={label}
      size="medium"
      items={searchableItems}
      required={required}
      disabled={disabled}
      component={FormSearchableControl}
      helperText={helperTextState}
      onBlur={handleOnBlur}
      dependentProps={['items', 'helperText']}
      onInputChange={(e: ChangeEvent, value: string) => {
        setHelperTextState(undefined);
        fieldHelper.setValue(value);
      }}
      clearOnBlur={false}
      acceptAnyValue
    />
  );
}
